import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Check, CreditCard, Sparkles, Award } from "lucide-react";
import { toast } from "sonner";

const FREE_FEATURES = [
  "5 AI Tutor questions / day",
  "2 Worksheet generations / day",
  "1 Quiz generation / day",
  "Student dashboard",
  "Parent dashboard",
  "Weekly parent reports",
  "Learning history & progress tracking",
  "Achievement badges & streaks",
];

const PREMIUM_FEATURES = [
  "Unlimited AI Tutor",
  "Unlimited Worksheets",
  "Unlimited Quizzes",
  "Unlimited Tests",
  "Unlimited Homework Help",
  "Unlimited English & French Practice",
  "Personalized Study Plans",
  "Priority processing",
  "Future Premium features included",
];

export default function TutorPricing() {
  const [plans, setPlans] = useState({});
  const [active, setActive] = useState(null);
  const [legacyPlan, setLegacyPlan] = useState(null); // for grandfathered subscribers
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/tutor/profile");
      setPlans(data.plans || {});
      setActive(data.subscription?.plan || null);
      // Legacy plan note: if their current sub is on a legacy plan, surface it
      if (data.subscription?.plan && data.all_plans?.[data.subscription.plan]?.is_legacy) {
        setLegacyPlan(data.all_plans[data.subscription.plan]);
      }
    } catch (e) {
      toast.error("Could not load pricing. Please refresh.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/api/tutor/subscribe", {
        plan, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally { setBusy(""); }
  };

  const premium = plans.premium_unlimited;

  return (
    <div data-testid="tutor-pricing">
      <div className="text-center">
        <div className="label-eyebrow flex items-center gap-2 justify-center"><Sparkles className="w-3 h-3 text-[#D92D20]" /> Simple pricing</div>
        <h1 className="font-display font-black text-3xl md:text-5xl mt-1">Free Forever, or Premium Unlimited.</h1>
        <p className="text-[#4B5563] mt-3 text-sm max-w-2xl mx-auto">
          Start learning today — no credit card required. Upgrade when you want unlimited everything.
        </p>
        <div className="inline-flex items-center gap-2 mt-4 px-3 py-1 rounded-full bg-[#D92D20]/10 text-[#D92D20] text-xs font-bold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Launch offer · save 50%
        </div>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* FREE FOREVER */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7 card-lift" data-testid="plan-free">
          <div className="flex items-center justify-between">
            <div className="font-display font-bold text-xl">Free Forever</div>
            <span className="text-[10px] font-bold uppercase tracking-widest bg-[#064E3B] text-white px-2 py-0.5 rounded-full">Start here</span>
          </div>
          <div className="mt-2 font-display font-black text-4xl">$0<span className="text-sm text-[#4B5563] font-normal">/month</span></div>
          <div className="text-xs text-[#4B5563] mt-1">Daily limits · No credit card required</div>
          <ul className="mt-5 text-sm space-y-2">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#064E3B] mt-0.5 flex-shrink-0" />{f}</li>
            ))}
          </ul>
          {active ? (
            <div className="mt-6 text-center text-sm font-semibold text-[#4B5563]" data-testid="plan-free-current">
              You&apos;re on Premium — enjoy unlimited!
            </div>
          ) : (
            <a href="/tutor/onboarding" data-testid="plan-free-cta" className="btn-outline mt-6 w-full inline-flex items-center justify-center gap-2">
              Get started — it&apos;s free
            </a>
          )}
          <div className="text-[10px] text-[#4B5563] text-center mt-2">Limits reset daily at midnight Eastern.</div>
        </div>

        {/* PREMIUM UNLIMITED */}
        {premium && (
        <div className="bg-white border-2 border-[#D92D20] rounded-2xl p-7 card-lift shadow-xl relative" data-testid="plan-premium_unlimited">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D92D20] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
            Most popular
          </div>
          <div className="flex items-center justify-between">
            <div className="font-display font-bold text-xl">{premium.name}</div>
            <Award className="w-5 h-5 text-[#D92D20]" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display font-black text-4xl">${premium.price}</span>
            <span className="text-sm text-[#4B5563]">/month</span>
            {premium.regular_price && (
              <span className="text-sm text-[#4B5563] line-through ml-1">${premium.regular_price}</span>
            )}
          </div>
          <div className="text-xs text-[#D92D20] font-bold mt-1">Launch offer — regular price ${premium.regular_price}/mo</div>
          <ul className="mt-5 text-sm space-y-2">
            {PREMIUM_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#D92D20] mt-0.5 flex-shrink-0" />{f}</li>
            ))}
          </ul>
          {active === "premium_unlimited" ? (
            <div className="mt-6 text-center text-sm font-semibold text-[#064E3B]" data-testid="plan-premium_unlimited-active">
              ✓ Active plan
            </div>
          ) : (
            <button data-testid="plan-premium_unlimited-subscribe" onClick={() => subscribe("premium_unlimited")}
              disabled={busy === "premium_unlimited"}
              className="btn-primary mt-6 w-full disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" /> {busy === "premium_unlimited" ? "Loading…" : "Upgrade to Premium"}
            </button>
          )}
          <div className="text-[10px] text-[#4B5563] text-center mt-2">Cancel anytime · No commitments</div>
        </div>
        )}
      </div>

      {/* Grandfathered legacy banner */}
      {legacyPlan && (
        <div className="max-w-3xl mx-auto mt-8 bg-[#FEF3C7] border border-[#FDE68A] rounded-2xl p-4 text-sm" data-testid="legacy-banner">
          <div className="font-semibold text-[#92400E]">You&apos;re on the legacy {legacyPlan.name} plan (${legacyPlan.price}/mo).</div>
          <p className="text-xs text-[#92400E]/80 mt-1">
            You keep your current plan and price for as long as you want — no need to change. If you&apos;d
            like to switch to the new Premium Unlimited at ${premium?.price}/mo, cancel your current
            plan from your dashboard and resubscribe here.
          </p>
        </div>
      )}

      <div className="mt-12 max-w-3xl mx-auto">
        <h3 className="font-display font-bold text-xl text-center">What free users get that other apps charge for</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4"><b>Parent reports</b> — emailed weekly with charts and progress.</div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4"><b>Achievement badges</b> — streaks, mastery, and milestones.</div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4"><b>Parent dashboard</b> — full visibility into what your child is studying.</div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4"><b>Canadian curriculum</b> — grade-aligned across all provinces.</div>
        </div>
      </div>

      <p className="text-center text-xs text-[#4B5563] mt-10">
        Free Forever stays free. Daily caps reset at midnight Eastern. Upgrade to Premium for unlimited access.
      </p>
    </div>
  );
}
